import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { LS } from 'src/app/helpers/localStorage.enum';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';
import { FileService } from 'src/app/services/file/file.service';
import { MapService } from 'src/app/services/map/map.service';
import { OpenlayerService } from 'src/app/services/openlayer/openlayer.service';
import { SplitViewService } from 'src/app/services/split-view/split-view.service';
import { SwalService } from 'src/app/services/swal/swal.service';
import { LayerStoreService } from 'src/app/stores/layer-store/layer-store.service';
import { PotreeCesiumViewerService } from '../potree-cesium-viewer/potree-cesium-viewer.service';

@Component({
  selector: 'app-card-annotation',
  templateUrl: './card-annotation.component.html',
  styleUrls: ['./card-annotation.component.sass'],
})
export class CardAnnotationComponent implements OnInit, OnDestroy {
  @Input() public annotation: any;
  @Input() public index: number;
  @Input() public canEdit: boolean = true;
  @Input() public review_module_enabled: boolean = false;
  @Output() public deleted = new EventEmitter();
  annotationServiceRemoveSub: Subscription;
  backgroundImage: any;
  backgroundImageWithoutURLContainer: string;
  annotationServiceFindSub: Subscription;
  chats: any[];
  isExtended: boolean = false;
  currentUser: any;
  permissions: any = {};
  map_layer_name = '';

  isSquareMeter = true;

  @Input() public canSelect: boolean = false;
  @Input() public selected: boolean = false;
  @Output() public onSelected = new EventEmitter();
  /**
   * Returns annotation location ID
   * Has a fallback for older data
   */
  get annotationLocId() {
    return this.annotation?.location?.id || this.annotation?.location;
  }

  constructor(
    private annotationService: AnnotationService,
    private fileService: FileService,
    private toastr: ToastrService,
    private layerStore: LayerStoreService,
    private mapService: MapService,
    private router: Router,
    private swalService: SwalService,
    private potreeCesiumViewerService: PotreeCesiumViewerService,
    private splitViewService: SplitViewService,
    private _t: TranslateService,
    private openlayerService: OpenlayerService
  ) {}

  ngOnInit(): void {
    this.currentUser = JSON.parse(localStorage.getItem(LS.currentUser));
    this.permissions = this.currentUser.permissions;

    const backgroundUrl =
      this.annotation.annotation_file?.url_medium ||
      this.annotation.annotation_file?.url ||
      this.annotation.area_thumbnail ||
      'assets/images/annotation.png';
    this.backgroundImage = `url(${backgroundUrl})`;
    this.backgroundImageWithoutURLContainer = backgroundUrl;

    let area;
    try {
      area = JSON.parse(this.annotation.area);
    } catch (error) {
      area = {};
    }
    console.log(area, this.annotation);
    if (this.annotation.map_layer) {
      this.map_layer_name = this.annotation.map_layer.layer_type
        ? this.annotation.map_layer.layer_type
        : this.annotation.map_layer.layer_type;
      this.canEdit = Object.keys(area).length > 0 ? true : false;
    } else if (
      area.length > 0 &&
      area[0].hasOwnProperty('className') &&
      area[0].hasOwnProperty('attrs')
    ) {
      this.map_layer_name = 'Gallery';
      this.canEdit = true;
    } else {
      this.map_layer_name = '-';
      this.canEdit = false;
    }
    this.isSquareMeter =
      area?.drawing && area.drawing[0]?.geometry?.type === 'LineString'
        ? false
        : true;
  }

  fallbackImg() {
    this.backgroundImageWithoutURLContainer = 'https://via.placeholder.com/150';
  }

  onEdit() {
    if (this.map_layer_name === 'Gallery') {
      this.router.navigate([
        '/projects',
        this.annotationLocId,
        'details',
        'gallery',
        {
          editAnnot: true,
          imageId: this.annotation.gallery_image,
          drawing: this.annotation.area,
        },
      ]);
      return;
    }
    // if (!this.canEdit){ //disable edit onsplitscreen
    this.router.navigate([
      '/projects',
      this.annotationLocId,
      'details',
      'annotations',
      'edit',
      this.annotation.id,
    ]);
    // }
  }

  async onDelete() {
    this.swalService.confirmDialog((swalRes) => {
      this.annotationServiceRemoveSub = this.annotationService
        .remove(this.annotation.id)
        .subscribe(async (res) => {
          swalRes();
          if (this.annotation.annotation_file?.id) {
            await this.fileService.remove(this.annotation.id).toPromise();
            this.openlayerService.removeOneAllSavedAnnot = this.annotation.area;
          }
          const undoMsg = this._t.instant('Tap HERE to undo');
          this.toastr
            .info(undoMsg, '', {
              progressBar: true,
              timeOut: 7000,
              enableHtml: true,
            })
            .onTap.pipe(take(1))
            .subscribe(() => {
              this.annotationService.restore(this.annotation.id).subscribe();
              this.openlayerService.appendOneAllSavedAnnot =
                this.annotation.area;
            });
          this.mapService.removeAllGrid();
          this.deleted.emit(true);
        });
    });
  }

  ngOnDestroy() {
    this.openlayerService.savedAnnot$.next({ zoomFitToFeature: undefined });
    if (this.annotationServiceRemoveSub)
      this.annotationServiceRemoveSub.unsubscribe();
    if (this.annotationServiceFindSub)
      this.annotationServiceFindSub.unsubscribe();
  }

  onClickThumbnail() {
    if (this.map_layer_name === 'Gallery') {
      this.router.navigate([
        '/projects',
        this.annotationLocId,
        'details',
        'gallery',
        {
          openModal: true,
          imageId: this.annotation.gallery_image,
          drawing: this.annotation.area,
        },
      ]);
      return;
    }
    this.splitViewService.onEvent$.next({
      onCardAnnotClicked: true,
      orthoLayer: this.annotation.map_layer,
    });
    const data = JSON.parse(this.annotation.area);
    this.layerStore.toggleMapLayer(
      this.annotation.map_layer.layer_type,
      this.annotation.location,
      this.annotation.map_layer,
      false
    );
    if (this.annotation.map_layer.layer_type === 'ortho') {
      this.mapService.fitboundCoordinates(
        data.drawing[0].geometry.type === 'LineString'
          ? data.drawing[0].geometry.coordinates
          : data.drawing[0].geometry.coordinates[0]
      );
      this.mapService._setImagesRelatedToCoor(data.drawing[0].geometry);
    }
    if (this.annotation.map_layer.layer_type === '3d') {
      this.potreeCesiumViewerService.loadMeasurements(
        JSON.parse(this.annotation.area)
      );
    }
    if (this.annotation.map_layer.layer_type === 'facade') {
      this.openlayerService.savedAnnot$.next({
        zoomFitToFeature: [this.annotation.area],
        openlayerDrawings: this.openlayerService.allSavedAnnot$.getValue(),
      });
    }
  }

  toggleExtended() {
    // this.isExtended = !this.isExtended
  }

  selectAnnotation(event) {
    event.preventDefault();

    this.onSelected.emit(event);
  }
}
